import { Undo2Icon } from 'lucide-react';
import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { undoFileEdit } from '@/lib/api/file-chat.js';
import { notify } from '@/lib/notify.js';

import type { UndoableEdit } from './file-edit.js';

/** Who asked for the edit and when, for the button's tooltip. */
export function undoTooltip(edit: UndoableEdit): string {
  const when = new Date(edit.at).toLocaleString([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const who = edit.askedBy ?? 'someone';
  return `Undo the edit ${who} asked the assistant for at ${when}`;
}

/**
 * Reverts the newest assistant edit of the open file. The server refuses when the file changed after
 * that edit, and that refusal is shown as it comes; anything older goes through Version history.
 */
export function UndoAssistantEditButton({
  edit,
  onUndone,
}: {
  edit: UndoableEdit;
  /** Called once the edit is undone, so the viewer reloads the file and drops the button. */
  onUndone: (edit: UndoableEdit) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const undo = async (): Promise<void> => {
    setBusy(true);
    try {
      await undoFileEdit(edit.conversationId, edit.messageId);
      onUndone(edit);
    } catch (error) {
      notify.error(
        error instanceof Error
          ? error.message
          : "Could not undo the assistant's edit",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="ghost"
      disabled={busy}
      title={undoTooltip(edit)}
      onClick={() => void undo()}
    >
      <Undo2Icon className="size-4" />
      Undo assistant edit
    </Button>
  );
}
